import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  Stack,
  Alert,
  Snackbar
} from '@mui/material';
import { Add, Edit, Delete, Phone, Email, ArrowUpward, ArrowDownward, LocationOn, Visibility } from '@mui/icons-material';
import { serviceProviderService, ServiceProvider } from '../../services/serviceProviderService';
import AreaSelectionMap, { AreaPolygon } from '../../components/service-providers/AreaSelectionMap';
import { usePagination } from '../../hooks/usePagination';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ServiceProviderList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [areaMapDialogOpen, setAreaMapDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'person' as 'company' | 'person',
    phone: '',
    email: '',
    address: '',
    area: '',
    isActive: true,
    notes: '',
    schedule: {},
    bankAccountNumber: '',
    bankName: '',
    bicCode: '',
    iban: ''
  });
  const [scheduleData, setScheduleData] = useState<any>({});
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: '*',
        'pagination[page]': pagination.page + 1,
        'pagination[pageSize]': pagination.pageSize,
        sort: `${sortField}:${sortOrder}`,
      };

      // Add search filter if search term exists
      if (searchTerm) {
        params['filters[$or][0][name][$containsi]'] = searchTerm;
        params['filters[$or][1][phone][$containsi]'] = searchTerm;
        params['filters[$or][2][email][$containsi]'] = searchTerm;
        params['filters[$or][3][area][$containsi]'] = searchTerm;
      }

      const response = await serviceProviderService.getAll(params);
      const providersData = response.data?.data || response.data || [];
      const paginationMeta = response.data?.meta?.pagination || response.meta?.pagination || {};
      
      setProviders(providersData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error: any) {
      console.error('Error loading service providers:', error);
      setError('Failed to load service providers');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, sortField, sortOrder, searchTerm, setPagination]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleSort = useCallback((field: string) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortOrder((prevOrder) => prevOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortOrder('asc');
      }
      return field;
    });
    // Reset to first page when sort changes
    setPagination({ page: 0 });
  }, [setPagination]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    // Reset to first page when search changes
    setPagination({ page: 0 });
  }, [setPagination]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      loadProviders();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, loadProviders]);

  const handleOpen = useCallback((provider?: any) => {
    if (provider) {
      setEditingProvider(provider);
      const attrs = provider.attributes || provider;
      setFormData({
        name: attrs.name || '',
        type: attrs.type || 'person',
        phone: attrs.phone || '',
        email: attrs.email || '',
        address: attrs.address || '',
        area: attrs.area || '',
        isActive: attrs.isActive !== undefined ? attrs.isActive : true,
        notes: attrs.notes || '',
        schedule: attrs.schedule || {},
        bankAccountNumber: attrs.bankAccountNumber || '',
        bankName: attrs.bankName || '',
        bicCode: attrs.bicCode || '',
        iban: attrs.iban || ''
      });
    } else {
      setEditingProvider(null);
      setFormData({
        name: '',
        type: 'person',
        area: '',
        phone: '',
        email: '',
        address: '',
        isActive: true,
        notes: '',
        schedule: {},
        bankAccountNumber: '',
        bankName: '',
        bicCode: '',
        iban: ''
      });
    }
    setError('');
    setOpen(true);
  }, []);

  const handleScheduleOpen = useCallback((provider: any) => {
    setEditingProvider(provider);
    const attrs = provider.attributes || provider;
    setScheduleData(attrs.schedule || {});
    setError('');
    setScheduleDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setEditingProvider(null);
  }, []);

  const handleScheduleClose = useCallback(() => {
    setScheduleDialogOpen(false);
    setEditingProvider(null);
  }, []);

  const handleAreaMapOpen = useCallback((provider: any) => {
    setEditingProvider(provider);
    setAreaMapDialogOpen(true);
  }, []);

  const handleAreaMapClose = useCallback(() => {
    setAreaMapDialogOpen(false);
    setEditingProvider(null);
  }, []);

  const handleAreaSave = async (areas: AreaPolygon[]) => {
    try {
      if (editingProvider) {
        console.log('Received areas to save:', areas);
        
        // Filter out polygons with empty coordinates and transform coordinates to number[][] format
        const transformedAreas = areas
          .filter(area => {
            const hasCoords = area.coordinates && Array.isArray(area.coordinates) && area.coordinates.length >= 3;
            if (!hasCoords) {
              console.warn('Filtering out area with invalid coordinates:', area);
            }
            return hasCoords;
          })
          .map(area => {
            const coords: [number, number][] = area.coordinates
              .map(coord => {
                if (Array.isArray(coord) && coord.length >= 2) {
                  const lat = Number(coord[0]);
                  const lng = Number(coord[1]);
                  if (isNaN(lat) || isNaN(lng)) {
                    console.warn('Invalid coordinate values:', coord);
                    return null;
                  }
                  return [lat, lng] as [number, number];
                }
                // Handle LatLng object format
                if (typeof coord === 'object' && coord !== null && 'lat' in coord && 'lng' in coord) {
                  const lat = Number((coord as any).lat);
                  const lng = Number((coord as any).lng);
                  if (isNaN(lat) || isNaN(lng)) {
                    console.warn('Invalid coordinate values:', coord);
                    return null;
                  }
                  return [lat, lng] as [number, number];
                }
                console.warn('Unknown coordinate format:', coord);
                return null;
              })
              .filter((coord): coord is [number, number] => coord !== null);
            
            if (coords.length < 3) {
              console.warn('Area has less than 3 valid coordinates:', area);
              return null;
            }
            
            return {
              id: area.id,
              name: area.name,
              coordinates: coords
            };
          })
          .filter((area): area is { id: string; name: string; coordinates: [number, number][] } => area !== null);
        
        console.log('Transformed areas:', transformedAreas);
        
        if (transformedAreas.length === 0) {
          setError('No valid areas to save. Please ensure you have completed at least one polygon with at least 3 points.');
          return;
        }
        
        // Store areas as JSON
        const areaData = {
          polygons: transformedAreas,
          // Also store a simple area string for backward compatibility
          areaString: transformedAreas.map(a => a.name).join(', ')
        };
        
        console.log('Saving area data:', JSON.stringify(areaData, null, 2));
        
        // Ensure the data structure matches what Strapi expects
        const updateData: any = {
          areaPolygons: areaData,
        };
        
        // Only update area if we have areas
        if (areaData.areaString) {
          updateData.area = areaData.areaString;
        }
        
        console.log('Update payload:', JSON.stringify(updateData, null, 2));
        
        const response = await serviceProviderService.update(editingProvider.id, updateData);
        console.log('Update response:', response);
        setSuccess('Service provider areas updated successfully');
        loadProviders();
        handleAreaMapClose();
      }
    } catch (error: any) {
      console.error('Error saving areas:', error);
      const errorMsg = error.response?.data?.error?.message || 'Failed to save areas';
      setError(errorMsg);
    }
  };

  const getProviderAreas = (provider: any): AreaPolygon[] => {
    const attrs = provider.attributes || provider;
    if (attrs.areaPolygons?.polygons) {
      // Filter out polygons with empty coordinates and ensure coordinates are in the correct format
      return attrs.areaPolygons.polygons
        .filter((poly: any) => poly.coordinates && Array.isArray(poly.coordinates) && poly.coordinates.length >= 3)
        .map((poly: any) => ({
          ...poly,
          coordinates: poly.coordinates || []
        }));
    }
    // Don't return legacy areas with empty coordinates - they should be drawn fresh
    return [];
  };

  const handleSubmit = useCallback(async () => {
    try {
      // Validate required fields
      if (!formData.name || !formData.phone) {
        setError('Name and phone are required');
        return;
      }
      
      // Validate email format if provided
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }

      if (editingProvider) {
        await serviceProviderService.update(editingProvider.id, formData);
        setSuccess('Service provider updated successfully');
      } else {
        await serviceProviderService.create(formData);
        setSuccess('Service provider created successfully');
      }
      loadProviders();
      handleClose();
    } catch (error: any) {
      console.error('Error saving service provider:', error);
      const errorMsg = error.response?.data?.error?.message || 'Failed to save service provider';
      setError(errorMsg);
    }
  }, [formData, editingProvider, loadProviders, handleClose]);

  const handleScheduleSubmit = useCallback(async () => {
    try {
      if (editingProvider) {
        // Normalize schedule data: ensure all active days have both from and to
        const normalizedSchedule: any = {};
        
        DAYS_OF_WEEK.forEach((day) => {
          const dayData = scheduleData[day];
          if (dayData?.active) {
            // Only include active days, and ensure they have both from and to
            normalizedSchedule[day] = {
              active: true,
              from: dayData.from || '08:00',
              to: dayData.to || '17:00'
            };
          }
        });
        
        console.log('Saving schedule:', normalizedSchedule);
        await serviceProviderService.update(editingProvider.id, { schedule: normalizedSchedule });
        setSuccess('Schedule updated successfully');
        loadProviders();
        handleScheduleClose();
      }
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      const errorMsg = error.response?.data?.error?.message || 'Failed to save schedule';
      setError(errorMsg);
    }
  }, [scheduleData, editingProvider, loadProviders, handleScheduleClose]);

  const handleDelete = useCallback(async (id: number) => {
    if (window.confirm('Are you sure you want to delete this service provider?')) {
      try {
        await serviceProviderService.delete(id);
        setSuccess('Service provider deleted successfully');
        loadProviders();
      } catch (error: any) {
        console.error('Error deleting service provider:', error);
        const errorMsg = error.response?.data?.error?.message || 'Failed to delete service provider';
        setError(errorMsg);
      }
    }
  }, [loadProviders]);

  const handleScheduleChange = (day: string, field: 'active' | 'from' | 'to', value: any) => {
    setScheduleData({
      ...scheduleData,
      [day]: {
        ...(scheduleData[day] || {}),
        [field]: value,
        // When activating a day, ensure default values are set if not already present
        ...(field === 'active' && value === true && !scheduleData[day]?.from && !scheduleData[day]?.to
          ? { from: '08:00', to: '17:00' }
          : {})
      }
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Service Providers</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
        >
          Add Service Provider
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search providers..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: '200px' }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('name')}
            startIcon={sortField === 'name' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Name
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('type')}
            startIcon={sortField === 'type' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Type
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('area')}
            startIcon={sortField === 'area' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Area
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Bank Account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.map((provider) => {
              const attrs = provider.attributes || provider;
              return (
              <TableRow key={provider.id}>
                <TableCell>{attrs.name}</TableCell>
                <TableCell>
                  <Chip label={attrs.type} size="small" />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone fontSize="small" />
                    {attrs.phone}
                  </Box>
                </TableCell>
                <TableCell>
                  {attrs.areaPolygons?.polygons && attrs.areaPolygons.polygons.length > 0 ? (
                    <Box>
                      {attrs.areaPolygons.polygons.slice(0, 2).map((poly: any) => (
                        <Chip key={poly.id} label={poly.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                      {attrs.areaPolygons.polygons.length > 2 && (
                        <Typography variant="caption" color="textSecondary">
                          +{attrs.areaPolygons.polygons.length - 2} more
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    attrs.area || '-'
                  )}
                </TableCell>
                <TableCell>
                  {attrs.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {attrs.email}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {attrs.bankAccountNumber ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {attrs.bankAccountNumber}
                      </Typography>
                      {attrs.bankName && (
                        <Typography variant="caption" color="textSecondary">
                          {attrs.bankName}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="textSecondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={attrs.isActive ? 'Active' : 'Inactive'}
                    color={attrs.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => navigate(`/service-providers/${provider.id}/overview`)} size="small" color="info" title="View Overview">
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleOpen(provider)} size="small" title="Edit">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleScheduleOpen(provider)} size="small" color="primary" title="Schedule">
                    <Typography variant="caption">Schedule</Typography>
                  </IconButton>
                  <IconButton onClick={() => handleAreaMapOpen(provider)} size="small" color="secondary" title="Select Areas">
                    <LocationOn />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(provider.id)} size="small" color="error" title="Delete">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.pageSize}
          page={pagination.page}
          onPageChange={(_, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
        />
      )}

      {!loading && providers.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No service providers found
          </Typography>
        </Paper>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProvider ? 'Edit Service Provider' : 'Add Service Provider'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'company' | 'person' })}
                >
                  <MenuItem value="person">Person</MenuItem>
                  <MenuItem value="company">Company</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={formData.email ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) : false}
                helperText={formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? 'Invalid email format' : ''}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Area"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
              />
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              Bank Account Information
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={formData.bankAccountNumber}
                onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
                placeholder="Account number for payments"
              />
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                placeholder="Name of the bank"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="BIC/SWIFT Code"
                value={formData.bicCode}
                onChange={(e) => setFormData({ ...formData, bicCode: e.target.value })}
                placeholder="e.g., BMUSOMRX"
                helperText="Bank Identifier Code"
              />
              <TextField
                fullWidth
                label="IBAN"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                placeholder="International Bank Account Number"
                helperText="If applicable"
              />
            </Box>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProvider ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scheduleDialogOpen} onClose={handleScheduleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Schedule - {editingProvider?.attributes?.name || editingProvider?.name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 2 }}>
            {DAYS_OF_WEEK.map((day) => (
              <Box key={day} sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={scheduleData[day]?.active || false}
                      onChange={(e) => handleScheduleChange(day, 'active', e.target.checked)}
                    />
                  }
                  label={day}
                />
                {scheduleData[day]?.active && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField
                      fullWidth
                      label="From"
                      type="time"
                      value={scheduleData[day]?.from || '08:00'}
                      onChange={(e) => handleScheduleChange(day, 'from', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="To"
                      type="time"
                      value={scheduleData[day]?.to || '17:00'}
                      onChange={(e) => handleScheduleChange(day, 'to', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleScheduleClose}>Cancel</Button>
          <Button onClick={handleScheduleSubmit} variant="contained">
            Save Schedule
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Dialog 
        open={areaMapDialogOpen} 
        onClose={handleAreaMapClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          Select Service Areas - {editingProvider?.attributes?.name || editingProvider?.name}
        </DialogTitle>
        <DialogContent dividers>
          {editingProvider && (
            <AreaSelectionMap
              initialAreas={getProviderAreas(editingProvider)}
              onSave={handleAreaSave}
              onCancel={handleAreaMapClose}
              providerName={editingProvider.attributes?.name || editingProvider.name || 'Service Provider'}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!error && !open && !scheduleDialogOpen && !areaMapDialogOpen}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceProviderList;

